import { Link } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { LogoMark } from '@/components/Logo';

export default function TermsOfServicePage() {
  return (
    <div className="min-h-screen bg-paper">
      {/* Header */}
      <header className="bg-white border-b border-line sticky top-0 z-50">
        <div className="max-w-4xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2.5">
            <LogoMark size={36} />
            <span className="text-lg font-bold font-display tracking-tight">
              <span className="text-ink">Portfolio</span>
              <span className="text-signal-blue"> Pal</span>
            </span>
          </Link>
          <Link 
            to="/" 
            className="flex items-center gap-2 text-sm text-muted-brand hover:text-ink transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to home
          </Link>
        </div>
      </header>

      {/* Content */}
      <main className="max-w-4xl mx-auto px-6 py-12">
        <article className="bg-white rounded-2xl border border-line p-8 md:p-12 shadow-sm">
          <h1 className="text-3xl md:text-4xl font-bold text-ink font-display tracking-tight mb-2">
            Terms of Service
          </h1>
          <p className="text-muted-brand mb-8">
            <strong>Effective Date:</strong> August 8, 2026
          </p>

          <div className="prose prose-slate max-w-none">
            <p className="text-slate-brand leading-relaxed mb-6">
              Welcome to Portfolio Pal. By accessing or using our web-based rental property management platform, you agree to be bound by these Terms of Service. If you do not agree with any part of these terms, you must not use our services.
            </p>

            <h2 className="text-xl font-semibold text-ink font-display mt-8 mb-4">
              1. Description of Service
            </h2>
            <p className="text-slate-brand leading-relaxed mb-6">
              Portfolio Pal is a rental property management tool designed to help landlords and property managers track properties, generate reports, parse documents, and manage lease agreements and tenant profiles.
            </p>

            <h2 className="text-xl font-semibold text-ink font-display mt-8 mb-4">
              2. User Accounts and Security
            </h2>
            <ul className="list-disc pl-6 space-y-3 text-slate-brand mb-6">
              <li>
                <strong className="text-ink">Registration:</strong> You must provide a valid email address to create an account.
              </li>
              <li>
                <strong className="text-ink">Security:</strong> You are responsible for maintaining the confidentiality of your account credentials. You agree to notify us immediately of any unauthorized use of your account.
              </li>
              <li>
                <strong className="text-ink">Permission Levels:</strong> Portfolio Pal utilizes tiered access roles. You are responsible for ensuring that the correct permissions are assigned to users accessing your property data.
              </li>
            </ul>

            <h2 className="text-xl font-semibold text-ink font-display mt-8 mb-4">
              3. User Data and Tenant Privacy
            </h2>
            <ul className="list-disc pl-6 space-y-3 text-slate-brand mb-6">
              <li>
                <strong className="text-ink">Compliance:</strong> As a user (landlord or property manager), you are solely responsible for ensuring that you have the legal right to collect, store, and process the personal information of your tenants (e.g., "Jill") within Portfolio Pal.
              </li>
              <li>
                <strong className="text-ink">Accuracy:</strong> You agree to input accurate property and financial data and maintain the integrity of the lease agreements generated or stored on the platform.
              </li>
            </ul>

            <h2 className="text-xl font-semibold text-ink font-display mt-8 mb-4">
              4. Third-Party Integrations
            </h2>
            <p className="text-slate-brand leading-relaxed mb-4">
              Portfolio Pal integrates with third-party services to enhance functionality, including:
            </p>
            <ul className="list-disc pl-6 space-y-3 text-slate-brand mb-4">
              <li>
                <strong className="text-ink">Google (Drive/Sheets)</strong> for document management and report data.
              </li>
              <li>
                <strong className="text-ink">Resend</strong> for email communications.
              </li>
            </ul>
            <p className="text-slate-brand leading-relaxed mb-6">
              Your use of these integrations may be subject to the respective terms and conditions of those third parties. Portfolio Pal is not responsible for the availability or reliability of these external services.
            </p>

            <h2 className="text-xl font-semibold text-ink font-display mt-8 mb-4">
              5. Acceptable Use
            </h2>
            <p className="text-slate-brand leading-relaxed mb-4">
              You agree not to use Portfolio Pal to:
            </p>
            <ul className="list-disc pl-6 space-y-3 text-slate-brand mb-6">
              <li>Store or transmit illegal, infringing, or defamatory material.</li>
              <li>Attempt to gain unauthorized access to the platform, its MongoDB database, or other users' tiered accounts.</li>
              <li>Interfere with or disrupt the integrity or performance of the platform.</li>
            </ul>

            <h2 className="text-xl font-semibold text-ink font-display mt-8 mb-4">
              6. Limitation of Liability
            </h2>
            <p className="text-slate-brand leading-relaxed mb-6">
              Portfolio Pal is provided on an "as is" and "as available" basis. To the fullest extent permitted by law, Portfolio Pal shall not be liable for any indirect, incidental, special, or consequential damages, including but not limited to loss of profits, data, or rental income resulting from the use or inability to use the service, errors in document parsing, or disputes between landlords and tenants.
            </p>

            <h2 className="text-xl font-semibold text-ink font-display mt-8 mb-4">
              7. Termination
            </h2>
            <p className="text-slate-brand leading-relaxed mb-6">
              We reserve the right to suspend or terminate your account at any time, with or without cause, including for any violation of these Terms of Service.
            </p>

            <h2 className="text-xl font-semibold text-ink font-display mt-8 mb-4">
              8. Governing Law
            </h2>
            <p className="text-slate-brand leading-relaxed mb-6">
              These Terms shall be governed by and construed in accordance with the laws of the United Kingdom, without regard to its conflict of law provisions.
            </p>

            <h2 className="text-xl font-semibold text-ink font-display mt-8 mb-4">
              Contact Us
            </h2>
            <p className="text-slate-brand leading-relaxed">
              If you have any questions about these Terms, please contact us at{' '}
              <a 
                href="mailto:williamhouse909@gmail.com" 
                className="text-signal-blue hover:text-anchor-blue transition-colors"
              >
                williamhouse909@gmail.com
              </a>.
            </p>
          </div>
        </article>
      </main>

      {/* Footer */}
      <footer className="py-8 px-6 border-t border-line bg-white">
        <div className="max-w-4xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-4">
          <Link to="/" className="flex items-center gap-2">
            <LogoMark size={28} />
            <span className="font-semibold font-display text-ink">Portfolio Pal</span>
          </Link>
          <div className="flex items-center gap-6 text-sm text-muted-brand">
            <Link to="/privacy" className="hover:text-ink transition-colors">Privacy Policy</Link>
            <Link to="/terms" className="hover:text-ink transition-colors">Terms of Service</Link>
          </div>
          <p className="text-sm text-muted-brand">
            © {new Date().getFullYear()} Portfolio Pal
          </p>
        </div>
      </footer>
    </div>
  );
}
